'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useLocalHref } from '../navigation/base-path';
import { cn } from '../utils/cn';

interface MobileWorkspaceHeaderProps {
  balance?: string;
  firstName?: string | null;
  photoUrl?: string | null;
  userId?: string | number | null;
}

function formatCurrency(amount: string | number): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return Number.isFinite(num) ? num.toLocaleString('ru-RU') : '';
}

function AnimatedBalance({
  balance,
  onLoaded,
}: {
  balance: string | undefined;
  onLoaded?: () => void;
}) {
  const prevBalanceRef = useRef<string | undefined>(undefined);
  const [animationClass, setAnimationClass] = useState('');
  const [displayBalance, setDisplayBalance] = useState(balance);
  const onLoadedRef = useRef(onLoaded);
  const hasReportedLoadRef = useRef(false);
  onLoadedRef.current = onLoaded;

  useEffect(() => {
    if (balance === undefined) return;
    const prevBalance = prevBalanceRef.current;

    if (prevBalance === undefined) {
      setDisplayBalance(balance);
      if (!hasReportedLoadRef.current) {
        hasReportedLoadRef.current = true;
        onLoadedRef.current?.();
      }
    } else if (prevBalance !== balance) {
      const prev = parseFloat(prevBalance);
      const curr = parseFloat(balance);
      if (curr < prev) setAnimationClass('animate-balance-decrease');
      else if (curr > prev) setAnimationClass('animate-balance-increase');
      setTimeout(() => setDisplayBalance(balance), 100);
      setTimeout(() => setAnimationClass(''), 600);
    }

    prevBalanceRef.current = balance;
  }, [balance]);

  return (
    <span className={cn('header-balance-text block font-benzin text-base font-semibold leading-none text-primary', animationClass)}>
      {displayBalance ? formatCurrency(displayBalance) : ''}
    </span>
  );
}

export function MobileWorkspaceHeader({
  balance,
  firstName,
  photoUrl,
  userId,
}: MobileWorkspaceHeaderProps) {
  const [balanceLoaded, setBalanceLoaded] = useState(false);
  const [balanceRevealReady, setBalanceRevealReady] = useState(false);
  const [headerRevealReady, setHeaderRevealReady] = useState(false);
  const [avatarVisible, setAvatarVisible] = useState(false);
  const [balanceContentWidth, setBalanceContentWidth] = useState(0);
  const balanceContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setHeaderRevealReady(false);

    const timerId = window.setTimeout(() => {
      setHeaderRevealReady(true);
    }, 160);

    return () => {
      window.clearTimeout(timerId);
    };
  }, []);

  useEffect(() => {
    if (balance === undefined) {
      setBalanceLoaded(false);
      setBalanceRevealReady(false);
    }
  }, [balance]);

  useEffect(() => {
    const node = balanceContentRef.current;
    if (!node) return;

    const updateWidth = () => {
      setBalanceContentWidth(node.scrollWidth);
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => updateWidth());
    observer.observe(node);
    return () => observer.disconnect();
  }, [balance]);

  useEffect(() => {
    // When we have a photoUrl, wait for the <Image> onLoad/onError to flip
    // avatarVisible so the fade-in is synced with the pixels. Otherwise
    // (loading, no-photo user, or failed /api/user/me) render the initial/
    // placeholder circle immediately — hiding the whole pill leaves the
    // mobile header looking broken when auth data is still in flight.
    if (photoUrl) {
      setAvatarVisible(false);
      return;
    }
    const frameId = window.requestAnimationFrame(() => setAvatarVisible(true));
    return () => window.cancelAnimationFrame(frameId);
  }, [firstName, photoUrl, userId]);

  useEffect(() => {
    if (!headerRevealReady || !balanceLoaded || balanceContentWidth === 0) {
      setBalanceRevealReady(false);
      return;
    }

    let frame1 = 0;
    let frame2 = 0;

    frame1 = window.requestAnimationFrame(() => {
      frame2 = window.requestAnimationFrame(() => setBalanceRevealReady(true));
    });

    return () => {
      window.cancelAnimationFrame(frame1);
      window.cancelAnimationFrame(frame2);
    };
  }, [headerRevealReady, balanceLoaded, balanceContentWidth]);

  const homeHref = useLocalHref('/');
  const creditsHref = useLocalHref('/credits');
  const profileHref = useLocalHref('/profile');

  const balanceSectionVisible = balanceRevealReady && balanceContentWidth > 0;
  const balanceShellWidth = balanceSectionVisible ? balanceContentWidth + 72 : 56;
  const balanceContentMotionStyle: CSSProperties = {
    opacity: balanceSectionVisible ? 1 : 0,
    transform: balanceSectionVisible ? 'translate3d(0, 0, 0) scale(1)' : 'translate3d(-6px, 0, 0) scale(0.985)',
    transformOrigin: 'left center',
    transition: [
      'opacity 240ms ease 90ms',
      'transform 420ms cubic-bezier(0.22, 1, 0.36, 1)',
    ].join(', '),
    willChange: 'opacity, transform',
  };

  return (
    <header className="relative z-10 flex h-[67px] items-center justify-between px-4">
      <Link href={homeHref} className="flex items-center">
        <Image
          src="/assets/Union.svg"
          alt="Микс"
          width={63}
          height={63}
          className="h-[63px] w-auto"
          priority
        />
      </Link>

      <div
        className="relative flex h-14 items-center overflow-hidden rounded-[56px_0_50px_56px] border border-black bg-black pr-1"
        style={{
          width: `${balanceShellWidth}px`,
          paddingLeft: balanceSectionVisible ? '12px' : '6px',
          transition: [
            'width 420ms cubic-bezier(0.22, 1, 0.36, 1)',
            'padding-left 420ms cubic-bezier(0.22, 1, 0.36, 1)',
          ].join(', '),
          willChange: 'width, padding-left',
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
        }}
      >
        <div className="pointer-events-none absolute right-[2px] top-[1px] h-[54px] w-[54px] rounded-full bg-yellow-400 opacity-20 blur-[14px]" />

        <div
          className="shrink-0 overflow-hidden"
          style={{
            width: balanceSectionVisible ? `${balanceContentWidth}px` : '0px',
            marginRight: balanceSectionVisible ? '10px' : '0px',
            transition: [
              'width 420ms cubic-bezier(0.22, 1, 0.36, 1)',
              'margin-right 420ms cubic-bezier(0.22, 1, 0.36, 1)',
            ].join(', '),
            willChange: 'width, margin-right',
          }}
        >
          <div
            ref={balanceContentRef}
            className="header-balance-row inline-flex min-h-[20px] items-center gap-[7px] whitespace-nowrap pr-1"
            style={balanceContentMotionStyle}
          >
            <Link href={creditsHref} className="flex h-[18px] shrink-0 items-center gap-[5px]">
              <svg
                aria-hidden="true"
                className="header-balance-gem block shrink-0"
                width={16}
                height={16}
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M6.99992 2L5.33325 6L7.99992 14.6667L10.6666 6L8.99992 2" stroke="#9DD94A" strokeLinecap="round" strokeLinejoin="round" />
                <path
                  d="M11.3332 2C11.5402 2 11.7444 2.04819 11.9295 2.14076C12.1147 2.23333 12.2757 2.36774 12.3999 2.53333L14.3999 5.2C14.5714 5.42878 14.6649 5.70663 14.6664 5.99257C14.668 6.2785 14.5776 6.55737 14.4086 6.788L9.0819 14.112C8.9584 14.2837 8.79584 14.4235 8.60765 14.5199C8.41946 14.6164 8.21103 14.6667 7.99956 14.6667C7.7881 14.6667 7.57966 14.6164 7.39147 14.5199C7.20328 14.4235 7.04073 14.2837 6.91723 14.112L1.59056 6.788C1.42164 6.55729 1.33135 6.27839 1.33303 5.99246C1.33471 5.70652 1.42827 5.4287 1.5999 5.2L3.59856 2.53533C3.72268 2.36919 3.88385 2.23429 4.06925 2.14136C4.25465 2.04843 4.45917 2.00003 4.66656 2H11.3332Z"
                  stroke="#9DD94A"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M1.33325 6H14.6666" stroke="#9DD94A" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="header-balance-text-wrap flex h-[18px] items-center">
                <AnimatedBalance balance={balance} onLoaded={() => setBalanceLoaded(true)} />
              </span>
            </Link>

            <Link href={creditsHref} className="header-balance-plus-link flex h-[18px] w-[17px] shrink-0 items-center justify-center">
              <svg
                className="header-balance-plus-icon block"
                width="17"
                height="17"
                viewBox="0 0 17 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <linearGradient id="bgGrad" x1="8.5" y1="0" x2="8.5" y2="17" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.12" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.04" />
                  </linearGradient>
                </defs>
                <circle cx="8.5" cy="8.5" r="8.35" fill="url(#bgGrad)" stroke="rgba(255,255,255,0.12)" strokeWidth="0.3" />
                <rect x="4.5" y="7.25" width="8" height="2.5" fill="white" />
                <rect x="7.25" y="4.5" width="2.5" height="8" fill="white" />
              </svg>
            </Link>
          </div>
        </div>

        <Link href={profileHref} className="relative z-10 flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted">
          <div
            className="h-full w-full"
            style={{
              opacity: avatarVisible ? 1 : 0,
              transform: avatarVisible ? 'scale(1)' : 'scale(0.92)',
              transition: 'opacity 220ms ease 120ms, transform 260ms cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          >
            {photoUrl ? (
              <Image
                key={photoUrl}
                src={photoUrl}
                alt="Avatar"
                width={44}
                height={44}
                className="h-full w-full object-cover"
                onLoad={() => setAvatarVisible(true)}
                onError={() => setAvatarVisible(true)}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-muted text-sm font-semibold text-muted-foreground">
                {firstName?.[0] || '?'}
              </div>
            )}
          </div>
        </Link>
      </div>
    </header>
  );
}
